import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Box, Typography, Paper, Checkbox, FormControlLabel } from '@mui/material';

export interface ColorRangeSelectorRef {
  getColorList: () => string[];
}

export interface ColorRangeSelectorProps {
  onChange?: (colors: { start: string; middle?: string; end: string }) => void;
}

export const ColorRangeSelector = forwardRef<ColorRangeSelectorRef, ColorRangeSelectorProps>(
  ({ onChange }, ref) => {
    const [startColor, setStartColor] = useState('#0000ff');
    const [endColor, setEndColor] = useState('#ff0000');
    const [middleColor, setMiddleColor] = useState('#00ff00');
    const [useMiddleColor, setUseMiddleColor] = useState(false);

    useImperativeHandle(ref, () => ({
      getColorList: () =>
        useMiddleColor
          ? [startColor, middleColor, endColor]
          : [startColor, endColor],
    }));

    useEffect(() => {
      if (onChange) {
        onChange(
          useMiddleColor
            ? { start: startColor, middle: middleColor, end: endColor }
            : { start: startColor, end: endColor },
        );
      }
    }, [startColor, middleColor, endColor, useMiddleColor, onChange]);

    return (
      <Paper
        variant='outlined'
        sx={{
          width: '100%',
          maxWidth: 320,
          p: 2,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          {/* Vertical gradient bar on left */}
          <Box
            sx={{
              height: 192,
              width: 24,
              borderRadius: 1,
              boxShadow: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.1)',
              backgroundImage: useMiddleColor
                ? `linear-gradient(to top, ${startColor}, ${middleColor}, ${endColor})`
                : `linear-gradient(to top, ${startColor}, ${endColor})`,
            }}
            aria-label='Color gradient preview'
            role='img'
          />

          {/* Color pickers aligned vertically */}
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: 192 }}>
            {/* High (top) */}
            <Box component='label' sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }}>
              <Typography variant='caption' sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                High
              </Typography>
              <Box
                component='input'
                type='color'
                value={endColor}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEndColor(e.target.value)}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  cursor: 'pointer',
                  p: 0,
                }}
                title='Pick High color'
              />
            </Box>

            {/* Middle (center) */}
            {useMiddleColor ? (
              <Box component='label' sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }}>
                <Typography variant='caption' sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                  Medium
                </Typography>
                <Box
                  component='input'
                  type='color'
                  value={middleColor}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMiddleColor(e.target.value)}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    p: 0,
                  }}
                  title='Pick Medium color'
                />
              </Box>
            ) : (
              <Box sx={{ width: 32, height: 32 }} />
            )}

            {/* Low (bottom) */}
            <Box component='label' sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }}>
              <Typography variant='caption' sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                Low
              </Typography>
              <Box
                component='input'
                type='color'
                value={startColor}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStartColor(e.target.value)}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  cursor: 'pointer',
                  p: 0,
                }}
                title='Pick Low color'
              />
            </Box>
          </Box>
        </Box>

        {/* Checkbox */}
        <FormControlLabel
          control={
            <Checkbox
              size='small'
              checked={useMiddleColor}
              onChange={(e) => setUseMiddleColor(e.target.checked)}
            />
          }
          label={<Typography variant='body2'>Add medium color</Typography>}
        />
      </Paper>
    );
  },
);

ColorRangeSelector.displayName = 'ColorRangeSelector';

export default ColorRangeSelector;
